(() => {
  'use strict';
  if (customElements.get('casemeta-product-navigation')) return;

  class CasemetaProductNavigation extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.listenForPageEvents();
        return;
      }
      this.initialized = true;
      this.select = this.querySelector('select');
      this.currentValue = this.select?.value;
      // Preserve only the theme preview context, never the source variant or
      // Customix query parameters. The destination starts with its own product.
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      const theme = window.Shopify?.theme;
      this.previewId = theme && theme.role !== 'main' ? String(theme.id) : preview;
      this.querySelectorAll('a[href], option[value]').forEach((element) => {
        const attribute = element.tagName === 'OPTION' ? 'value' : 'href';
        const destination = this.destination(element.getAttribute(attribute));
        if (destination) element.setAttribute(attribute, destination.href);
      });
      this.currentValue = this.select?.value;
      this.select?.addEventListener('change', () => {
        if (this.select.value === this.currentValue) return;
        const destination = this.destination(this.select.value);
        if (destination) window.location.assign(destination.href);
      });
      // BFCache restores the old document and can preserve the changed select.
      this.restoreSelection = () => {
        if (this.select) this.select.value = this.currentValue;
        this.closePicker();
      };
      this.createBrandPicker();
      this.listenForPageEvents();
    }

    disconnectedCallback() {
      this.pageEvents?.abort();
    }

    listenForPageEvents() {
      this.pageEvents?.abort();
      this.pageEvents = new AbortController();
      const { signal } = this.pageEvents;
      window.addEventListener('pageshow', this.restoreSelection, { signal });
      if (!this.picker) return;
      document.addEventListener('pointerdown', (event) => {
        if (!this.picker.contains(event.target)) this.closePicker();
      }, { signal });
      document.addEventListener('focusin', (event) => {
        if (!this.picker.contains(event.target)) this.closePicker();
      }, { signal });
    }

    createBrandPicker() {
      if (!this.select) return;
      // Section labels come from the merchant's group data. Combine repeated
      // labels so a brand split into several reference lists still has one tab.
      const groups = new Map();
      for (const option of this.select.options) {
        const brand = option.closest('optgroup')?.label?.trim();
        // Mixed/uncategorized data keeps the complete native dropdown.
        if (!brand) return;
        if (!groups.has(brand)) groups.set(brand, []);
        groups.get(brand).push(option);
      }
      if (groups.size < 2) return;

      const current = this.select.selectedOptions[0];
      if (!current) return;
      this.currentBrand = current.closest('optgroup').label.trim();
      const make = (tag, className, text) => {
        const element = document.createElement(tag);
        element.className = className;
        if (text) element.textContent = text;
        return element;
      };
      const picker = make('div', 'cm-product-nav__picker');
      const summary = make('button', 'cm-product-nav__select cm-product-nav__trigger');
      summary.type = 'button';
      summary.setAttribute('aria-expanded', 'false');
      const value = make('span', 'cm-product-nav__value', current.textContent.trim());
      value.id = `${this.select.id}-value`;
      summary.append(value, this.select.parentElement.querySelector('svg').cloneNode(true));
      const label = this.querySelector(`label[for="${this.select.id}"]`);
      if (label) {
        label.removeAttribute('for');
        label.id = `${this.select.id}-label`;
        summary.setAttribute('aria-labelledby', `${label.id} ${value.id}`);
      }
      const panel = make('div', 'cm-product-nav__panel');
      panel.id = `${this.select.id}-panel`;
      panel.hidden = true;
      summary.setAttribute('aria-controls', panel.id);
      const brands = make('div', 'cm-product-nav__brands');
      brands.setAttribute('role', 'group');
      brands.setAttribute('aria-label', 'Telefon markası');
      const modelList = make('ul', 'cm-product-nav__models');
      modelList.id = `${this.select.id}-models`;
      this.brandButtons = [];
      this.brandModels = new Map();
      for (const [brand, options] of groups) {
        const button = make('button', 'cm-product-nav__brand', brand);
        button.type = 'button';
        button.dataset.brand = brand;
        button.setAttribute('aria-controls', modelList.id);
        button.addEventListener('click', () => this.showBrand(brand));
        brands.append(button);
        this.brandButtons.push(button);
        this.brandModels.set(brand, options.map((option) => ({
          label: option.textContent.trim(),
          url: option.value,
          productId: option.dataset.productId,
        })));
      }
      panel.append(brands, modelList);
      picker.append(summary, panel);
      this.picker = picker;
      this.panel = panel;
      this.trigger = summary;
      this.modelList = modelList;
      this.showBrand(this.currentBrand);
      summary.addEventListener('click', () => {
        if (!panel.hidden) {
          this.closePicker();
          return;
        }
        this.showBrand(this.currentBrand);
        panel.hidden = false;
        summary.setAttribute('aria-expanded', 'true');
      });
      picker.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !panel.hidden) {
          event.preventDefault();
          event.stopPropagation();
          this.closePicker();
          summary.focus();
        }
      });
      this.select.closest('.cm-product-nav__select-wrap').after(picker);
      this.select.closest('.cm-product-nav__select-wrap').hidden = true;
    }

    showBrand(brand) {
      if (this.displayedBrand === brand) return;
      const models = this.brandModels.get(brand);
      if (!models) return;
      this.displayedBrand = brand;
      this.brandButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.brand === brand));
      });
      // Render only the chosen brand. Do not repeatedly hide/reveal every
      // model on focus changes or use the browser's native details toggle.
      const items = models.map((model) => {
        const item = document.createElement('li');
        item.className = 'cm-product-nav__model-item';
        const link = document.createElement('a');
        link.className = 'cm-product-nav__model-link';
        link.textContent = model.label;
        link.href = model.url;
        link.dataset.productId = model.productId;
        if (model.url === this.currentValue) {
          link.setAttribute('aria-current', 'page');
          // Choosing the displayed product just closes the picker. It does
          // not issue an unnecessary reload or discard native variant state.
          link.addEventListener('click', (event) => {
            if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            this.closePicker();
            this.trigger.focus();
          });
        }
        item.append(link);
        return item;
      });
      this.modelList.replaceChildren(...items);
      this.modelList.setAttribute('aria-label', `${brand} modelleri`);
      this.modelList.scrollTop = 0;
    }

    closePicker() {
      if (!this.panel || this.panel.hidden) return;
      this.panel.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    destination(path) {
      if (!path) return null;
      const url = new URL(path, window.location.origin);
      if (url.origin !== window.location.origin || !/\/products\/[^/]+$/.test(url.pathname)) return null;
      url.search = '';
      url.hash = '';
      if (this.previewId && /^\d+$/.test(this.previewId)) url.searchParams.set('preview_theme_id', this.previewId);
      return url;
    }
  }
  customElements.define('casemeta-product-navigation', CasemetaProductNavigation);
})();
